import React, { useCallback, useEffect, useRef, useState } from "react";

import { SearchIcon } from "@heroicons/react/outline";

import ApiService from "../services/ApiService";
import Controlbar from "../components/bundel/Controlbar";
import EasyTrans from "../components/bundel/EasyTrans";
import Spinner from "../components/bundel/Spinner";
import WorkGridCard from "../components/bundel/WorkGridCard";
import WorkGridSkeleton from "../components/bundel/WorkGridSkeleton";
import { EmptyState } from "../components/ui";

const ITEMS_PER_PAGE = 20;

const SearchWorksPage = () => {
  const [works, setWorks] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [title, setTitle] = useState();
  const [workType, setWorkType] = useState();
  const [licence, setLicence] = useState();
  const [sort, setSort] = useState("asc");
  const [selectedTags, setSelectedTags] = useState([]);

  const sentinelRef = useRef(null);

  const fetchPage = useCallback(
    async (pageToLoad, { append }) => {
      append ? setIsLoadingMore(true) : setIsLoading(true);
      try {
        const result = await ApiService.getPublicWorkList(
          title,
          workType,
          licence,
          selectedTags,
          pageToLoad,
          ITEMS_PER_PAGE,
          sort,
        );
        const content = result?.content ?? [];
        setTotalPages(result?.totalPages ?? 0);
        setWorks((prev) => (append ? [...prev, ...content] : content));
      } catch (error) {
        console.error("Error:", error);
      } finally {
        append ? setIsLoadingMore(false) : setIsLoading(false);
      }
    },
    [title, workType, licence, selectedTags, sort],
  );

  // live search: refetch the first page whenever a filter changes (title typing
  // is debounced so we don't fire on every keystroke). Also covers mount.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchPage(0, { append: false });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, workType, licence, sort, selectedTags]);

  // append when the page counter advances (infinite scroll)
  useEffect(() => {
    if (page > 0) fetchPage(page, { append: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const hasMore = page < totalPages - 1;

  // auto-load the next page as the sentinel scrolls into view
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore]);

  return (
    <div>
      <Controlbar
        onSetTitle={setTitle}
        onSetWorkType={setWorkType}
        onSetLicence={setLicence}
        onSetSort={setSort}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
      />
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 sm:my-6 lg:my-10">
        {isLoading ? (
          <WorkGridSkeleton />
        ) : works.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {works.map((work, index) => (
                <WorkGridCard key={work.id ?? index} work={work} />
              ))}
            </div>
            {/* infinite-scroll sentinel + loading indicator */}
            <div ref={sentinelRef} className="h-10" />
            {isLoadingMore && (
              <div className="flex justify-center pb-6">
                <Spinner />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={SearchIcon}
            title={<EasyTrans>NoElementsFound</EasyTrans>}
            hint={<EasyTrans>NoElementsFoundHint</EasyTrans>}
          />
        )}
      </div>
    </div>
  );
};

export default SearchWorksPage;
